<template>
  <div>
    <b-row>
      <!-- Date Range Preset -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Date Range</label>
        <v-select
          v-model="filters.datePreset"
          :options="datePresetOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="Select Range"
          :clearable="false"
          @input="applyDatePreset"
        />
        <small v-if="remainingNarrowing" class="text-muted d-block mt-25">
          Wider than Last 7 Days — pick {{ remainingNarrowing }} more filter{{ remainingNarrowing === 1 ? '' : 's' }} below.
        </small>
      </b-col>

      <!-- Start Date -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">From</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="date-input"
          @change="filters.datePreset = 'custom'"
        />
      </b-col>

      <!-- End Date -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">To</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="date-input"
          @change="filters.datePreset = 'custom'"
        />
      </b-col>

      <!-- Call Status Filter -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Call Status</label>
        <v-select
          v-model="filters.status"
          :options="statusOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Statuses"
          :clearable="false"
        />
      </b-col>

      <!-- Direction Filter -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Direction</label>
        <v-select
          v-model="filters.direction"
          :options="directionOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Directions"
          :clearable="false"
        />
      </b-col>

      <!-- Manager filter — visible to admins in org or team mode -->
      <b-col v-if="isAdminLike && (viewMode === 'org' || viewMode === 'team')" cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Manager</label>
        <v-select
          v-model="filters.manager"
          :options="managerOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Managers"
          :clearable="false"
        />
      </b-col>

      <!-- Recruiter filter — visible to team leads / account managers in team mode, admins in team/org -->
      <b-col v-if="(canManageCalls || $can('read', 'account_manager')) && (viewMode === 'team' || viewMode === 'org')" cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Recruiter</label>
        <v-select
          v-model="filters.recruiter"
          :options="recruiterOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Recruiters"
          :clearable="false"
        />
      </b-col>

      <!-- Client — filters by job/client_name on enriched call rows -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Client</label>
        <v-select
          v-model="filters.client"
          :options="clientOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Clients"
          :clearable="false"
        />
      </b-col>

      <!-- Vertical — jobs config (Service / Infra Industry), not invoice type -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Vertical</label>
        <v-select
          v-model="filters.vertical"
          :options="resolvedVerticalOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Verticals"
          :clearable="false"
        />
      </b-col>

      <!-- Category — jobs config (White / Blue Collar) -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Category</label>
        <v-select
          v-model="filters.category"
          :options="resolvedCategoryOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Categories"
          :clearable="false"
        />
      </b-col>

      <!-- Candidate Status — pipeline status on the call row (not Call Status). -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Candidate Status</label>
        <v-select
          v-model="filters.candidateStatus"
          :options="resolvedCandidateStatusOptions"
          :reduce="opt => opt.value"
          label="text"
          placeholder="All Candidate Statuses"
          :clearable="false"
        />
      </b-col>

      <!-- Search — History + Team / Datewise / Hourly / Analysis. Not applied to KPI cards. -->
      <b-col cols="12" md="4" class="mb-1">
        <label class="d-block small font-weight-bold mb-50">Search</label>
        <input
          v-model="filters.search"
          type="search"
          class="date-input"
          placeholder="Name, phone, email…"
        />
      </b-col>
    </b-row>

    <hr class="my-1" />

    <b-alert
      v-if="wideRangeFilterError"
      show
      variant="warning"
      class="mb-1 py-75"
    >
      {{ wideRangeFilterError }}
    </b-alert>

    <div class="d-flex flex-wrap justify-content-end" style="gap:8px;">
      <b-button variant="outline-secondary" size="sm" @click="$emit('reset')">
        <feather-icon icon="XIcon" class="mr-25" />
        Reset
      </b-button>
      <b-button variant="primary" size="sm" :disabled="!!wideRangeFilterError" @click="applyAndClose">
        <feather-icon icon="CheckIcon" class="mr-25" />
        Apply Filters
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  requiredNarrowingCount,
  inclusiveDaySpan,
  describeWideRangeFilterError,
  last7InclusiveStart,
  selectedNarrowingFilters,
  DEFAULT_JOB_VERTICALS,
  DEFAULT_JOB_CATEGORIES,
  buildCandidateStatusFilterOptions,
} from '../utils/call-filter-scope'

// IST-aware date helpers — the API/DB timestamps are IST-based (see parseISTDate
// in MyCalls.vue), so date-range presets must be computed against the Asia/Kolkata
// calendar day rather than the browser's local timezone or UTC (which toISOString
// would otherwise produce, silently shifting the "today" boundary for users whose
// browser clock isn't IST).
function getISTDateParts(d = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d)
  const map = {}
  parts.forEach(p => { map[p.type] = p.value })
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) }
}
function istDateObj(d = new Date()) {
  const { year, month, day } = getISTDateParts(d)
  return new Date(Date.UTC(year, month - 1, day))
}
function fmt(d) { return d.toISOString().slice(0, 10) }
function todayStr() { return fmt(istDateObj()) }
function offsetDays(n) { const d = istDateObj(); d.setUTCDate(d.getUTCDate() + n); return fmt(d) }
function startOfWeek() { const d = istDateObj(); d.setUTCDate(d.getUTCDate() - d.getUTCDay()); return fmt(d) }
function startOfMonth() { const d = istDateObj(); d.setUTCDate(1); return fmt(d) }

const DATE_PRESET_OPTIONS = Object.freeze([
  { value: 'today', text: 'Today' },
  { value: 'yesterday', text: 'Yesterday' },
  { value: 'this_week', text: 'This Week' },
  { value: 'last_7', text: 'Last 7 Days' },
  { value: 'this_month', text: 'This Month' },
  { value: 'last_30', text: 'Last 30 Days' },
  { value: 'last_90', text: 'Last 90 Days' },
  { value: 'custom', text: 'Custom' },
])
const STATUS_OPTIONS = Object.freeze([
  { value: 'all', text: 'All Statuses' },
  { value: 'answered', text: 'Answered' },
  { value: 'not_answered', text: 'Not Answered' },
  { value: 'failed', text: 'Failed' },
  { value: 'busy', text: 'Busy' },
])
const DIRECTION_OPTIONS = Object.freeze([
  { value: 'all', text: 'All Directions' },
  { value: 'outbound', text: 'Outbound' },
  { value: 'inbound', text: 'Inbound' },
])

export default {
  name: 'MyCallsFilters',

  components: {
    BRow,
    BCol,
    BButton,
    BAlert,
    vSelect,
  },

  props: {
    filters: { type: Object, required: true },
    appliedFilters: { type: Object, required: true },
    viewMode: { type: String, required: true },
    isAdminLike: { type: Boolean, default: false },
    recruiterOptions: { type: Array, default: () => [] },
    managerOptions: { type: Array, default: () => [] },
    clientOptions: { type: Array, default: () => [] },
    verticalOptions: { type: Array, default: () => [] },
    categoryOptions: { type: Array, default: () => [] },
    candidateStatusOptions: { type: Array, default: () => [] },
  },

  data() {
    return {
      datePresetOptions: DATE_PRESET_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      directionOptions: DIRECTION_OPTIONS,
    }
  },

  computed: {
    canManageCalls() {
      return this.$can('manage', 'call_analytics')
    },
    showManagerFilter() {
      return this.isAdminLike && (this.viewMode === 'org' || this.viewMode === 'team')
    },
    showRecruiterFilter() {
      return (this.canManageCalls || this.$can('read', 'account_manager'))
        && (this.viewMode === 'team' || this.viewMode === 'org')
    },
    resolvedVerticalOptions() {
      return this.verticalOptions && this.verticalOptions.length
        ? this.verticalOptions
        : [
          { value: 'all', text: 'All Verticals' },
          ...DEFAULT_JOB_VERTICALS.map(v => ({ value: v, text: v })),
        ]
    },
    resolvedCategoryOptions() {
      return this.categoryOptions && this.categoryOptions.length
        ? this.categoryOptions
        : [
          { value: 'all', text: 'All Categories' },
          ...DEFAULT_JOB_CATEGORIES.map(v => ({ value: v, text: v })),
        ]
    },
    resolvedCandidateStatusOptions() {
      return this.candidateStatusOptions && this.candidateStatusOptions.length
        ? this.candidateStatusOptions
        : buildCandidateStatusFilterOptions()
    },
    narrowingOptions() {
      return {
        includeManager: this.showManagerFilter,
        includeRecruiter: this.showRecruiterFilter,
      }
    },
    dateSpanDays() {
      return inclusiveDaySpan(this.filters.startDate, this.filters.endDate)
    },
    requiredNarrowing() {
      return requiredNarrowingCount(this.dateSpanDays)
    },
    remainingNarrowing() {
      if (!this.requiredNarrowing) return 0
      const have = selectedNarrowingFilters(this.filters, this.narrowingOptions).length
      return Math.max(0, this.requiredNarrowing - have)
    },
    wideRangeFilterError() {
      return describeWideRangeFilterError(this.filters, this.narrowingOptions)
    },
  },

  methods: {
    applyDatePreset(preset) {
      const today = todayStr()
      if (preset === 'today') {
        this.filters.startDate = today
        this.filters.endDate = today
      } else if (preset === 'yesterday') {
        const yesterday = offsetDays(-1)
        this.filters.startDate = yesterday
        this.filters.endDate = yesterday
      } else if (preset === 'this_week') {
        this.filters.startDate = startOfWeek()
        this.filters.endDate = today
      } else if (preset === 'last_7') {
        this.filters.startDate = last7InclusiveStart(today)
        this.filters.endDate = today
      } else if (preset === 'this_month') {
        this.filters.startDate = startOfMonth()
        this.filters.endDate = today
      } else if (preset === 'last_30') {
        this.filters.startDate = offsetDays(-30)
        this.filters.endDate = today
      } else if (preset === 'last_90') {
        this.filters.startDate = offsetDays(-90)
        this.filters.endDate = today
      }
    },

    applyAndClose() {
      if (this.wideRangeFilterError) {
        this.$bvToast.toast(this.wideRangeFilterError, {
          title: 'Add more filters',
          variant: 'warning',
          solid: true,
          autoHideDelay: 8000,
        })
        return
      }
      this.$emit('apply')
      this.$bvModal.hide('modal-filters')
    },
  },
}
</script>

<style scoped>
.date-input {
  display: block;
  width: 100%;
  min-height: 38px;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #6e6b7b;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out;
  appearance: none;
  -webkit-appearance: none;
}

.date-input:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: none;
}

.dark-layout .date-input {
  background-color: #283046;
  border-color: #404656;
  color: #b4b7bd;
}
</style>
